import React from "react"
import { TimeZoneType } from "../../constants/timezones"
import { IOptionNormalized } from "../../options/interfaces/options-normalized"
import { makeLayout } from "./layout"
import {
  createTreegrid,
  destroyTreegrid,
  getSummaryTextWithRowCount,
  syncDataFromServerToGrid,
} from "../utils/tree-grid"
import { useTranslations } from "../hooks/use-translations"
import {
  ActivityColumnName,
  ActivityRow,
  CalendarEventActivityRow,
  ProjectActivityRow,
  TaskActivityRow,
  TodoActivityRow,
} from "./types"
import {
  getColorFromColorHtml,
  getShouldRemoveEventRowBasedOnContext,
  getShouldRemoveProjectRowBasedOnContext,
  getShouldRemoveTaskRowBasedOnContext,
  getStateFromStateIcon,
  mapTranslationsToColor,
} from "../utils"
import { DateTimeService } from "../../services/date-time-service"
import { useTreeGridFilters } from "../hooks/use-treegrid-filters"
import { useUrlWithContext } from "../../hooks/use-url-with-context"
import { useRouter } from "../../hooks"
import { makeProjectActivityRow } from "./rows/project-activity-row"
import { makeTodoActivityRow } from "./rows/todo-activity-row"
import { makeTaskActivityRow } from "./rows/task-activity-row"
import { makeCalendarEventActivityRow } from "./rows/calendar-event-activity-row"
import { useAppContext } from "../../app-context/use-app-context"
import { useContextOptions } from "../../context-options/hooks/use-context-options"
import { useAuthUserMembership } from "../../memberships/hooks/use-auth-user-membership"
import { paths } from "../../paths"
import { CalendarEventViewModel } from "../../calendar-events/api/calendar-event"
import { ProjectViewModel } from "../../projects/api/project"
import { TodoViewModel } from "../../todos/api/todo"
import { TaskViewModel } from "../../tasks/api/task"
import { useCalendarEventMutations } from "../../calendar-events/hooks/use-calendar-event-mutations"
import { useProjectMutations } from "../../projects/hooks/use-project-mutations"
import { useTodoMutations } from "../../todos/hooks/use-todo-mutations"
import { useTaskMutations } from "../../tasks/hooks/use-task-mutations"
import { useUpdateTaskStatus } from "../../tasks/hooks/use-update-task-status"
import { useUpdateProjectStatus } from "../../projects/hooks/use-update-project-status"
import { getMaxDate, getMinDate } from "../../utils"
import { SystemStatus } from "../../lib/system-status"
import { HolidayViewModel } from "../../holidays/api/holiday"
import { OptionType } from "../../tasks/api/task-response"
import { getGanttVisibleRange } from "../utils/gantt-utils"
import { makeOnSearchChange } from "../base/event-handlers/on-search-change"
import { makeOnFilterFinish } from "../base/event-handlers/on-filter-finish"
import { makeOnAfterSectionResize } from "../base/event-handlers/on-after-section-resize"
import { CalendarSettingsViewModel } from "../../calendar-settings/api/calendar-settings"
import { makeCalendarSettingsId } from "../../calendar-settings/api/calendar-settings-id"
import { useCanEditDateByCalendarSettingsId } from "../hooks/use-can-edit-date-by-calendar-settings-id"
import { useSnackbar } from "notistack"
import { useTranslation } from "react-i18next"
import { useFileDownload } from "../../storage/hooks/use-file-download"

const id = "__treegrid_activities_list__"

const useActivitiesList = (props: ActivitiesListProps) => {
  const { calendarEvents, projects, tasks, todos, options, holidays = [] } = props
  const { dateFormat, dateSeparator, firstDayOfWeek, timeZone, weekendDays, gridInfo } = props
  const projectTranslations = useTranslation("project").t
  const { enqueueSnackbar } = useSnackbar()
  const { initCalendarEventUpdate } = useCalendarEventMutations()
  const { initProjectUpdate } = useProjectMutations()
  const { initTaskUpdate } = useTaskMutations()
  const { initTodoUpdate } = useTodoMutations()
  const { updateTaskStatus, updatedTasks, resetUpdatedTasksAfterSync } = useUpdateTaskStatus()
  const { updateProjectStatus, updatedProject, updatedTasks: updatedProjectTasks } = useUpdateProjectStatus()
  const { showFilters, toggleFilters } = useTreeGridFilters({ gridId: id })
  const [navigateTo, setNavigateTo] = React.useState<string | null>(null)
  const zoomRef = React.useRef<string | null>(null)
  const { createPathWithContext } = useUrlWithContext()
  const { appContext } = useAppContext()
  const { userMembershipContextOptions } = useContextOptions()
  const { history } = useRouter()
  const translations = useTranslations()
  const { openFileInBrowser } = useFileDownload()

  const authUserMembership = useAuthUserMembership()
  const canCreateProjects = Boolean(appContext.mainContext?.type === "user" || authUserMembership?.canCreateProjects)
  const canEditDateByCalendarSettingsId = useCanEditDateByCalendarSettingsId(props.calendarSettingsById)

  translations.toolbarActivitiesListSummaryText = getSummaryTextWithRowCount(
    translations.toolbarActivitiesListSummaryText,
    props.todos.length + props.projects.length
  )
  const projectGroupHeader = `<h4 class="group-header">${translations.activitiesListProjectGroupHeader}</h4>`
  const taskTodoAndCalendarEventGroupHeader = `<h4 class="group-header">${translations.activitiesListTaskTodoAndCalendarEventGroupHeader}</h4>`

  const { minStartDate, maxEndDate } = getGanttMinStartAndMaxEndDates({
    projects,
    tasks,
    todos,
    events: calendarEvents,
    timeZone,
  })

  const layout = makeLayout({
    id,
    isFilterRowVisible: showFilters,
    canCreateProjects,
    dateFormat,
    dateSeparator,
    firstDayOfWeek,
    gridInfo,
    translations,
    options,
    weekendDays,
    holidays,
    timeZone,
    showGantt: true,
    showGanttToolbarActions: true,
    ganttZoomLeftEdgeRefDate: minStartDate || new Date(),
    ganttZoomRightEdgeRefDate: maxEndDate || new Date(),
  })
  const leftColumnCount = layout.LeftCols?.length || layout.Cols.length

  const memoizedRows = React.useMemo(() => {
    const calendarEventRows = calendarEvents.map((calendarEvent) => {
      return makeCalendarEventActivityRow({ calendarEvent, dateFormat, timeZone, options, translations })
    })
    const projectRows = projects.map((project) =>
      makeProjectActivityRow({ project, dateFormat, timeZone, options, translations })
    )
    const todoRows = todos.map((todo) => makeTodoActivityRow({ todo, dateFormat, timeZone, options, translations }))
    const taskRows = tasks.map((task) => makeTaskActivityRow({ task, dateFormat, timeZone, options, translations }))
    const shouldGroupRows =
      projectRows.length > 0 && (todoRows.length > 0 || taskRows.length > 0 || calendarEventRows.length > 0)

    if (shouldGroupRows) {
      const projectRootRow = {
        Spanned: 1,
        openSpan: leftColumnCount,
        open: projectGroupHeader,
        openType: "Html",
        Items: projectRows,
      }

      const taskTodoAndCalendarEventRootRow = {
        Spanned: 1,
        openSpan: leftColumnCount,
        open: taskTodoAndCalendarEventGroupHeader,
        openType: "Html",
        Items: [...taskRows, ...todoRows, ...calendarEventRows],
      }
      return [projectRootRow, taskTodoAndCalendarEventRootRow]
    } else {
      return [...projectRows, ...taskRows, ...todoRows, ...calendarEventRows]
    }
  }, [
    calendarEvents,
    projects,
    todos,
    tasks,
    dateFormat,
    timeZone,
    options,
    translations,
    leftColumnCount,
    projectGroupHeader,
    taskTodoAndCalendarEventGroupHeader,
  ])

  // @ts-ignore
  window.Grids.OnSearchChange = makeOnSearchChange({ translations })
  window.Grids.OnFilterFinish = makeOnFilterFinish({ translations, gridId: "home" })
  window.Grids.OnAfterSectionResize = makeOnAfterSectionResize()

  window.Grids.OnCanEditDate = function (grid, row: TRow & ActivityRow, col, date) {
    const projectCalendarSettingsId = getProjectCalendarSettingsId(row)
    const canEditDate = projectCalendarSettingsId ? canEditDateByCalendarSettingsId[projectCalendarSettingsId] : null
    if (canEditDate) return canEditDate(date)

    return true
  }

  window.Grids.OnValueChanged = function (
    grid: TGrid,
    row: TRow & ActivityRow,
    column: ActivityColumnName,
    newValue,
    oldValue
  ) {
    // NOTE: Returning early from this function will cause unexpected behavior in the treegrid
    // @ts-ignore
    const isEditAllowed = row[column + "CanEdit"] === 1
    const hasValueChanged = newValue !== oldValue
    const shouldDispatchUpdateRequest = isEditAllowed && hasValueChanged
    if (shouldDispatchUpdateRequest) {
      let value = adaptValue({ row, column, timeZone: props.timeZone, value: newValue, oldValue })
      if (column === "ganttBarColor") {
        row.GanttGanttClass = value === "Default" ? "Blue" : value // changes the color of the gantt bar immediately
        value = mapTranslationsToColor(translations)[value]
      }
      // Special handling for multi-select columns to change the value structure
      // This logic is only for projects and tasks because calendar events
      // and todo's dont have RTK optimistic query enabled
      if (
        column === "customers" ||
        column === "participants" ||
        column === "suppliers" ||
        column === "workspaces" ||
        column === "responsible"
      ) {
        if (isProjectRow(row) || isTaskRow(row)) {
          const oldIds: string[] = oldValue.split(";").filter(Boolean)
          const newIds: string[] = newValue.split(";").filter(Boolean)

          const add = newIds
            .filter((id) => !oldIds.includes(id))
            .map((id) => {
              const option = options.find((opt) => opt.id === id)
              return { id, name: option?.name ?? "" }
            })

          const remove = oldIds
            .filter((id) => !newIds.includes(id))
            .map((id) => {
              const option = options.find((opt) => opt.id === id)
              return { id, name: option?.name ?? "" }
            })

          value = { add, remove }
        }
        if (isCalendarEventRow(row) || isTodoRow(row)) {
          // Split the new value string into IDs
          const ids: string[] = newValue.split(";").filter(Boolean)

          // Map IDs to full option objects (id + name)
          const updatedArray: OptionType[] = ids.map((id) => {
            const option = options.find((opt) => opt.id === id)
            return { id, name: option?.name ?? "" }
          })

          value = updatedArray
        }
      }
      if (isCalendarEventRow(row)) {
        const field = activityColumnToCalendarEventFieldMap[column]
        initCalendarEventUpdate(row.id, { field, value }).then((calendarEvent) => {
          if (!calendarEvent || !grid) return
          // For recurring events, only the next upcoming occurrence is shown in activities list
          // so we reset the calendar event's start and end date to next occurrence's start and end date to show the correct data in the row
          const eventToUpdate =
            calendarEvent.isRecurringEvent && calendarEvent.nextOccurrence
              ? {
                  ...calendarEvent,
                  startDate: calendarEvent.nextOccurrence.startDate,
                  endDate: calendarEvent.nextOccurrence.endDate,
                }
              : calendarEvent

          const calendarEventRow = makeCalendarEventActivityRow({
            calendarEvent: eventToUpdate,
            dateFormat,
            timeZone,
            options,
            translations,
          })
          const shouldRemoveRow = getShouldRemoveEventRowBasedOnContext(
            calendarEvent,
            appContext,
            userMembershipContextOptions
          )
          if (shouldRemoveRow) {
            grid.AnimateRow(row, "Delete", undefined, () => grid.RemoveRow(row))
          } else {
            syncDataFromServerToGrid(grid, [calendarEventRow])
          }
        })
      }
      if (isTodoRow(row)) {
        const field = activityColumnToTodoFieldMap[column]
        initTodoUpdate(row.id, { field, value }).then((responseData) => {
          if (!responseData || !grid) return
          const context = appContext.subContext || appContext.mainContext
          const userMembershipIds = userMembershipContextOptions?.map((option) => option.id)
          const todo = "todo" in responseData ? responseData.todo : responseData
          const isVisibleInThisContext = todo.checkIsVisibleInContext(context, userMembershipIds)
          const isVisible = isVisibleInThisContext || !todo.isCompleted

          if (isVisible) {
            const todoRow = makeTodoActivityRow({ todo, dateFormat, timeZone, options, translations })
            syncDataFromServerToGrid(grid, [todoRow])
          } else {
            grid.AnimateRow(row, "Delete", undefined, () => grid.RemoveRow(row))
          }
        })
      }
      if (isProjectRow(row)) {
        const field = activityColumnToProjectFieldMap[column]
        if (field === "status") {
          const [mainStatusId, subStatusId] = newValue.toString().split("_")
          const status = SystemStatus.statusByIndex[parseInt(mainStatusId)]
          updateProjectStatus(row.id, { status, subStatusId: subStatusId ?? null })
        }

        if (field === "actualStartDate") {
          const startRaw = value ?? row.actualStartDate
          let sendReq = true
          // actual start and end dates cannot be in the future
          if (startRaw) {
            const startISO = startRaw ? DateTimeService.toLocalISOStringFormat(new Date(startRaw)) : ""
            const nowISO = row.enableTimeComponent
              ? DateTimeService.toLocalISOStringFormat(new Date())
              : new Date().toISOString()
            // actual start date cannot be in the future
            if (startISO && startISO > nowISO) {
              enqueueSnackbar(projectTranslations("projectDetails.actualStartDateCannotBeInFuture"), {
                variant: "error",
              })
              value = oldValue === "" ? "" : new Date(oldValue).toISOString()
              sendReq = false
            }
          }
          // If the value is valid and has passed all the validations,
          // then we can send the update request to the server. If not,
          // we should revert back the value in the grid and not send the request.
          if (sendReq) {
            updateProjectStatus(row.id, { [column]: value })
            return
          }
          return value ? new Date(value) : ""
        } else {
          initProjectUpdate(row.id, { field, value }).then((responseData) => {
            if (!responseData || !grid) return

            // First take care of updating or removing the project row
            const project = "project" in responseData ? responseData.project : responseData
            // PRESERVE LINKS AND FILES FROM ORIGINAL PROJECT
            const enrichedProject = getEnrichedProjects([project], props.projects)[0]
            const shouldRemoveProjectRowByContext = getShouldRemoveProjectRowBasedOnContext(
              enrichedProject,
              appContext,
              userMembershipContextOptions
            )
            const shouldRemoveProjectRowByStatus = !(enrichedProject.isNotStarted || enrichedProject.isInProgress)
            const shouldRemoveProjectRow = shouldRemoveProjectRowByContext || shouldRemoveProjectRowByStatus

            if (shouldRemoveProjectRow) {
              grid.AnimateRow(row, "Delete", undefined, () => grid.RemoveRow(row))
            } else {
              const projectRow = makeProjectActivityRow({
                project: enrichedProject,
                dateFormat,
                timeZone,
                options,
                translations,
              })
              syncDataFromServerToGrid(grid, [projectRow])
            }

            // Then take care of updating or removing the task rows
            const tasks = "project" in responseData ? responseData.tasks : []
            // PRESERVE LINKS AND FILES FROM ORIGINAL TASKS
            const enrichedTasks = getEnrichedTasks(tasks, props.tasks)

            const taskRowsToUpdateInGrid: TaskActivityRow[] = []
            const tasksToRemoveFromGrid: TaskViewModel[] = []

            enrichedTasks.forEach((task) => {
              const shouldRemoveRow = getShouldRemoveTaskRowBasedOnContext(
                task,
                appContext,
                userMembershipContextOptions
              )
              if (shouldRemoveRow) tasksToRemoveFromGrid.push(task)
              else
                taskRowsToUpdateInGrid.push(makeTaskActivityRow({ task, dateFormat, timeZone, options, translations }))
            })
            syncDataFromServerToGrid(grid, taskRowsToUpdateInGrid)
            tasksToRemoveFromGrid.forEach((task) => {
              const row = grid.GetRowById(task.id)
              if (row) grid.AnimateRow(row, "Delete", undefined, () => grid.RemoveRow(row))
            })
          })
        }
      }
      if (isTaskRow(row)) {
        const field = activityColumnToTaskFieldMap[column]
        if (field === "customers") return
        if (field === "status") {
          const [mainStatusId, subStatusId] = newValue.toString().split("_")
          const status = SystemStatus.statusByIndex[parseInt(mainStatusId)]
          updateTaskStatus(row.id, { status, subStatusId: subStatusId ?? null })
          return newValue
        }
        if (field === "actualStartDate") {
          const startRaw = value
          let sendReq = true
          // actual start and end dates cannot be in the future
          if (startRaw) {
            const startISO = startRaw ? DateTimeService.toLocalISOStringFormat(new Date(startRaw)) : ""
            const nowISO = row.enableTimeComponent
              ? DateTimeService.toLocalISOStringFormat(new Date())
              : new Date().toISOString()
            // actual start date cannot be in the future
            if (startISO && startISO > nowISO) {
              enqueueSnackbar(projectTranslations("projectDetails.actualStartDateCannotBeInFuture"), {
                variant: "error",
              })
              value = oldValue === "" ? "" : new Date(oldValue).toISOString()
              sendReq = false
            }
          }
          // If the value is valid and has passed all the validations,
          // then we can send the update request to the server. If not,
          // we should revert back the value in the grid and not send the request.
          if (sendReq) {
            updateTaskStatus(row.id, { [column]: value })
            return
          }
          return value ? new Date(value) : ""
        } else {
          initTaskUpdate(row.id, { field, value }).then((responseData) => {
            if (!responseData || !grid) return
            const tasks = "tasks" in responseData ? responseData.tasks : [responseData]
            // PRESERVE LINKS AND FILES FROM ORIGINAL TASKS
            const enrichedTasks = getEnrichedTasks(tasks, props.tasks)
            const taskRowsToUpdateInGrid: TaskActivityRow[] = []
            const tasksToRemoveFromGrid: TaskViewModel[] = []
            enrichedTasks.forEach((task) => {
              const shouldRemoveRow = getShouldRemoveTaskRowBasedOnContext(
                task,
                appContext,
                userMembershipContextOptions
              )
              if (shouldRemoveRow) tasksToRemoveFromGrid.push(task)
              else
                taskRowsToUpdateInGrid.push(makeTaskActivityRow({ task, dateFormat, timeZone, options, translations }))
            })
            syncDataFromServerToGrid(grid, taskRowsToUpdateInGrid)
            tasksToRemoveFromGrid.forEach((task) => {
              const row = grid.GetRowById(task.id)
              if (row) grid.AnimateRow(row, "Delete", undefined, () => grid.RemoveRow(row))
            })
          })
        }
      }
    }
    return newValue
  }

  // @ts-ignore
  window.Grids["OnFilterToggle"] = function () {
    toggleFilters()
  }

  window.Grids.OnClick = function (grid: TGrid, row: TRow, col: string) {
    // Disable header click for open column
    if (col === "open" && row.Kind === "Header") {
      return true
    }
    if (col === "AddProject") setNavigateTo(paths.newProject())
    if (col === "open") {
      if (isProjectRow(row as unknown as ActivityRow)) setNavigateTo(paths.projectBasic(row.id))
      if (isTaskRow(row as unknown as ActivityRow)) setNavigateTo(paths.taskBasic(row.id))
    }
    if (col === "ZoomFit" && grid) {
      const ganttZoomRange = getGanttVisibleRange({
        startDate: new Date(),
        endDate: maxEndDate,
      })
      grid.ZoomTo(ganttZoomRange.start, ganttZoomRange.end)
      return true
    }
    if (col === "linksAndAttachments") {
      const event = window.event
      const target = event?.target as HTMLElement
      const fileLink = target?.closest("[data-file-id]")
      if (fileLink) {
        const fileId = fileLink.getAttribute("data-file-id")
        if (fileId) {
          openFileInBrowser(fileId)
          return true
        }
      }
    }
  }

  window.Grids.OnTip = function (grid: TGrid, row: TRow & ActivityRow, column: ActivityColumnName) {
    if (column === "status" && isTodoRow(row)) {
      return row.status ? translations.markTodoAsNotCompletedTip : translations.markTodoAsCompletedTip
    }
  }

  window.Grids.OnGetSortValue = function (grid, row, column, value) {
    if (column === "plannedStartDate" || column === "plannedEndDate") {
      const shouldSortToBottom = !value
      if (shouldSortToBottom) return Number.MAX_SAFE_INTEGER
    }
    return value
  }

  window.Grids.OnRenderFinish = function (grid) {
    if (grid) {
      const ganttZoomRange = getGanttVisibleRange()
      grid.ZoomTo(ganttZoomRange.start, ganttZoomRange.end)
    }
  }

  window.Grids.OnZoom = function (_, zoom) {
    zoomRef.current = zoom
  }

  // @ts-ignore
  window.Grids.onClickTitleLink = function (link) {
    const linkWithContext = createPathWithContext(link)
    history.push(linkWithContext)
  }

  React.useEffect(() => {
    if (navigateTo) history.push(createPathWithContext(navigateTo))
  }, [navigateTo, history, createPathWithContext])

  React.useEffect(() => {
    const grid = window.Grids[id]
    if (grid) {
      const taskRowsToUpdate: TaskActivityRow[] = []
      const enrichedTasks = getEnrichedTasks(updatedTasks, props.tasks)
      enrichedTasks.forEach((task) => {
        if (task.isNotStarted || task.isInProgress) {
          const row = grid.GetRowById(task.id)
          if (row) taskRowsToUpdate.push(makeTaskActivityRow({ task, dateFormat, timeZone, options, translations }))
        } else {
          const row = grid.GetRowById(task.id)
          if (row) grid.AnimateRow(row, "Delete", undefined, () => grid.RemoveRow(row))
        }
      })
      syncDataFromServerToGrid(grid, taskRowsToUpdate)
      resetUpdatedTasksAfterSync()
    }
  }, [updatedTasks, dateFormat, timeZone, options, translations, resetUpdatedTasksAfterSync, props.tasks])

  React.useEffect(() => {
    const grid = window.Grids[id]
    if (grid && updatedProject) {
      // PRESERVE LINKS AND FILES FROM ORIGINAL PROJECT
      const enrichedProject = getEnrichedProjects([updatedProject], props.projects)[0]
      const shouldUpdateRow = enrichedProject.isNotStarted || enrichedProject.isInProgress
      if (shouldUpdateRow) {
        const projectRow = makeProjectActivityRow({
          project: enrichedProject,
          dateFormat,
          timeZone,
          options,
          translations,
        })
        syncDataFromServerToGrid(grid, [projectRow])
      } else {
        const row = grid.GetRowById(enrichedProject.id)
        if (row) grid.AnimateRow(row, "Delete", undefined, () => grid.RemoveRow(row))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedProject])

  React.useEffect(() => {
    const grid = window.Grids[id]
    if (grid) {
      const taskRowsToUpdate: TaskActivityRow[] = []
      // PRESERVE LINKS AND FILES FROM ORIGINAL TASKS
      const enrichedTasks = getEnrichedTasks(updatedProjectTasks, props.tasks)

      enrichedTasks.forEach((task) => {
        if (task.isNotStarted || task.isInProgress) {
          const row = grid.GetRowById(task.id)
          if (row) taskRowsToUpdate.push(makeTaskActivityRow({ task, dateFormat, timeZone, options, translations }))
        } else {
          const row = grid.GetRowById(task.id)
          if (row) grid.AnimateRow(row, "Delete", undefined, () => grid.RemoveRow(row))
        }
      })
      syncDataFromServerToGrid(grid, taskRowsToUpdate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedProjectTasks])

  // Adding keydown listener to handle ctrl/cmd + c and ctrl/cmd + v for copy paste functionality,
  // and stopping the event propagation so that it doesn't trigger any other copy paste handlers
  // in the app when the treegrid is focused.
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "c" || e.key === "v")) {
        const grid = window.Grids[id]
        if (grid) {
          e.stopImmediatePropagation()
        }
      }
    }
    document.addEventListener("keydown", handleKeyDown, true) // true = capture phase
    return () => document.removeEventListener("keydown", handleKeyDown, true)
  }, [])

  return { layout, data: memoizedRows }
}

const ActivitiesList = (props: ActivitiesListProps) => {
  const { layout, data } = useActivitiesList(props)

  React.useEffect(() => {
    createTreegrid({ id, layout, data })
    return () => destroyTreegrid(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <div id={id} style={{ height: "calc(100vh - 128px)" }} data-test="activities-list-container"></div>
}

export default ActivitiesList

function adaptValue({
  row,
  column,
  value,
  timeZone,
  oldValue,
}: {
  row: ActivityRow
  column: string
  timeZone: TimeZoneType
  value: any
  oldValue: any
}) {
  if (isDateColumn(column)) return adaptDateValue({ value, timeZone })
  if (column === "state") return getStateFromStateIcon(value)
  if (column === "ganttBarColor") return getColorFromColorHtml(value)
  if (isSelectionColumn(column)) {
    if (isProjectRow(row) || isTaskRow(row)) {
      const oldArray: string[] = oldValue.split(";").filter(Boolean)
      const newArray: string[] = value.split(";").filter(Boolean)
      const add = newArray.filter((x) => !oldArray.includes(x))
      const remove = oldArray.filter((x) => !newArray.includes(x))
      return { add, remove }
    } else {
      return adaptStringToArray(value)
    }
  }
  return value
}

function isDateColumn(columnName: string) {
  return columnName === "actualStartDate" || columnName === "plannedEndDate" || columnName === "plannedStartDate"
}

function isSelectionColumn(columnName: string) {
  return (
    columnName === "responsible" ||
    columnName === "participants" ||
    columnName === "suppliers" ||
    columnName === "workspaces" ||
    columnName === "customers"
  )
}

function adaptDateValue({ value, timeZone }: { value: any; timeZone: TimeZoneType }): string | null {
  if (value) {
    const dateTimeService = new DateTimeService({ timeZone })
    return dateTimeService.addTimezoneOffset(new Date(value), "UTC").toISOString()
  }
  return ""
}

function adaptStringToArray(value: any): string[] {
  return value.split(";").filter(Boolean)
}

function isTodoRow(row: ActivityRow): row is TodoActivityRow {
  return row.type === "todo"
}

function isProjectRow(row: ActivityRow): row is ProjectActivityRow {
  return row.type === "project"
}

function isTaskRow(row: ActivityRow): row is TaskActivityRow {
  return row.type === "task"
}

function isCalendarEventRow(row: ActivityRow): row is CalendarEventActivityRow {
  return row.type === "calendarEvent"
}

function getProjectCalendarSettingsId(row: ActivityRow): string | null {
  if (isProjectRow(row)) return makeCalendarSettingsId({ type: "project", id: row.id })
  if (isTaskRow(row)) return makeCalendarSettingsId({ type: "project", id: row.projectId })
  return null
}

function getGanttMinStartAndMaxEndDates(props: {
  projects: ProjectViewModel[]
  tasks: TaskViewModel[]
  todos: TodoViewModel[]
  events: CalendarEventViewModel[]
  timeZone: TimeZoneType
}): { minStartDate: Date | null; maxEndDate: Date | null } {
  const { projects, tasks, todos, events } = props
  const projectPlannedStartDates = projects.map((project) => project.plannedStartDate).filter(Boolean)
  const projectPlannedEndDates = projects.map((project) => project.plannedEndDate).filter(Boolean)
  const projectActualStartDates = projects.map((project) => project.actualStartDate).filter(Boolean)
  const taskPlannedStartDates = tasks.map((task) => task.plannedStartDate).filter(Boolean)
  const taskActualStartDates = tasks.map((task) => task.actualStartDate).filter(Boolean)
  const taskPlannedEndDates = tasks.map((task) => task.plannedEndDate).filter(Boolean)
  const todoDueDates = todos.map((todo) => todo.dueDate).filter(Boolean) as string[]

  const eventsToDisplay = events.filter((event) => {
    if (!event.isRecurringEvent) return true
    if (event.nextOccurrence) return true
    return false
  })
  const eventStartDates = eventsToDisplay
    .map((event) => {
      if (event.isRecurringEvent) return event.nextOccurrence?.startDate
      return event.startDate
    })
    .filter(Boolean) as string[]
  const eventEndDates = eventsToDisplay
    .map((event) => {
      if (event.isRecurringEvent) return event.nextOccurrence?.endDate
      return event.endDate
    })
    .filter(Boolean) as string[]

  const allDates = [
    ...projectPlannedEndDates,
    ...projectPlannedStartDates,
    ...projectActualStartDates,
    ...taskPlannedStartDates,
    ...taskPlannedEndDates,
    ...taskActualStartDates,
    ...todoDueDates,
    ...eventStartDates,
    ...eventEndDates,
  ]

  let minStartDate = getMinDate(allDates)
  let maxEndDate = getMaxDate(allDates)

  return {
    minStartDate,
    maxEndDate,
  }
}
const getEnrichedProjects = (updatedProjects: ProjectViewModel[], allProjects: ProjectViewModel[]) => {
  // Enrich updated projects with links and files from original data
  // The API response may not include these, so we preserve them from the source
  return updatedProjects.map((updatedProject) => {
    const originalProject = allProjects.find((p) => p.id === updatedProject.id)
    if (originalProject) {
      return {
        ...updatedProject,
        links: originalProject.links || [],
        files: originalProject.files || [],
        canViewProjectAttachments: originalProject.canViewProjectAttachments,
      }
    }
    return updatedProject
  })
}
const getEnrichedTasks = (updatedTasks: TaskViewModel[], allTasks: TaskViewModel[]) => {
  // Enrich updated tasks with links and files from original data
  // The API response may not include these, so we preserve them from the source
  return updatedTasks.map((updatedTask) => {
    const originalTask = allTasks.find((t) => t.id === updatedTask.id)
    if (originalTask) {
      return {
        ...updatedTask,
        links: originalTask.links || [],
        files: originalTask.files || [],
        canViewTaskAttachments: originalTask.canViewTaskAttachments,
      }
    }
    return updatedTask
  })
}
const activityColumnToTodoFieldMap: Record<string, TodoField> = {
  customers: "customers",
  title: "title",
  description: "description",
  responsible: "responsible",
  workspaces: "workspaces",
  plannedEndDate: "dueDate",
  status: "completed",
  ganttBarColor: "ganttBarColor",
}

const activityColumnToProjectFieldMap: Record<string, ProjectField> = {
  customers: "customers",
  activityNumber: "projectNumber",
  title: "title",
  description: "description",
  responsible: "managers",
  participants: "participants",
  workspaces: "workspaces",
  suppliers: "suppliers",
  plannedStartDate: "plannedStartDate",
  plannedEndDate: "plannedEndDate",
  actualStartDate: "actualStartDate",
  status: "status",
  state: "state",
  statusDescription: "statusDescription",
  ganttBarColor: "ganttBarColor",
  completionPercentage: "completionPercentage",
}

const activityColumnToTaskFieldMap: Record<string, TaskField> = {
  customers: "customers",
  activityNumber: "taskNumber",
  title: "title",
  description: "description",
  responsible: "managers",
  participants: "participants",
  workspaces: "workspaces",
  suppliers: "suppliers",
  plannedStartDate: "plannedStartDate",
  plannedEndDate: "plannedEndDate",
  actualStartDate: "actualStartDate",
  status: "status",
  state: "state",
  statusDescription: "statusDescription",
  ganttBarColor: "ganttBarColor",
  completionPercentage: "completionPercentage",
}

const activityColumnToCalendarEventFieldMap: Record<string, CalendarEventField> = {
  customers: "customers",
  title: "title",
  description: "description",
  participants: "participants",
  workspaces: "workspaces",
  suppliers: "suppliers",
  plannedStartDate: "startDate",
  plannedEndDate: "endDate",
  ganttBarColor: "color",
}

type CalendarEventField =
  | "customers"
  | "title"
  | "description"
  | "participants"
  | "workspaces"
  | "suppliers"
  | "startDate"
  | "endDate"
  | "color"

type ProjectField =
  | "customers"
  | "projectNumber"
  | "title"
  | "description"
  | "managers"
  | "participants"
  | "workspaces"
  | "suppliers"
  | "plannedStartDate"
  | "plannedEndDate"
  | "actualStartDate"
  | "status"
  | "state"
  | "statusDescription"
  | "ganttBarColor"
  | "completionPercentage"

type TodoField =
  | "customers"
  | "title"
  | "description"
  | "responsible"
  | "workspaces"
  | "dueDate"
  | "completed"
  | "ganttBarColor"

type TaskField =
  | "customers"
  | "taskNumber"
  | "title"
  | "description"
  | "managers"
  | "participants"
  | "workspaces"
  | "suppliers"
  | "plannedStartDate"
  | "plannedEndDate"
  | "actualStartDate"
  | "status"
  | "state"
  | "statusDescription"
  | "ganttBarColor"
  | "completionPercentage"

type ActivitiesListProps = {
  calendarEvents: CalendarEventViewModel[]
  projects: ProjectViewModel[]
  todos: TodoViewModel[]
  tasks: TaskViewModel[]
  holidays?: HolidayViewModel[]
  dateFormat: string
  dateSeparator: string
  firstDayOfWeek: DayOfWeek
  gridInfo: string
  timeZone: TimeZoneType
  options: IOptionNormalized[]
  weekendDays: DayOfWeek[]
  calendarSettingsById: Record<string, CalendarSettingsViewModel>
}
